import { useQueryClient } from '@tanstack/react-query';
import { InfoCircle } from '@untitledui/icons';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';

import { useUser } from '@/allauth_auth/hooks.js';
import { Button } from '@/lib/base/buttons/button';
import { TextArea } from '@/lib/base/forms/textarea/textarea';
import { useLatestRef } from '@/lib/hooks/useLatestRef';
import { logger } from '@/lib/logger';
import { track } from '@/lib/mixpanel';
import { useStickyFooterToastLift } from '@/lib/proposals/hooks/useStickyFooterToastLift';
import {
  actionsLockMessage,
  getActionsLockReason,
} from '@/lib/proposals/utils/clarifyingGating';
import { ConversationInput } from '@/lib/reasoning/ConversationInput';
import { PROPOSAL_DECISION_TIMING, trackTiming } from '@/lib/timing';

import { useProposalSidebar } from '../../contexts/ActionSidebarContext';
import {
  getUserFriendlyMessage,
  isProposalAPIError,
} from '../../lib/proposals/core/errors';
import { createProposalClient } from '../../lib/proposals/core/ProposalClient';
import type {
  GmailSendPayload,
  ProposalDetail,
  ProposalExecutionStatus,
  ProposedAction,
} from '../../lib/proposals/core/types';
import {
  type CalendarEventCreatePayload,
  isCalendarEventCreatePayload,
} from '../../lib/proposals/core/types';
import { isDestructiveCrmPayload } from '../../lib/proposals/core/types';
import { useNoOpDismissOptional } from '../../lib/proposals/hooks/NoOpDismissContext';
import { useProposalSelection } from '../../lib/proposals/hooks/ProposalSelectionContext';
import { useExecutionPolling } from '../../lib/proposals/hooks/useExecutionPolling';
import { useProposalDecision } from '../../lib/proposals/hooks/useProposalActions';
import { proposalQueryKeys } from '../../lib/proposals/query/keys';
import { submitApprovalInBackground } from '../../lib/proposals/query/submitApprovalInBackground';
import { lookupAction } from '../../lib/proposals/registry/utils';
import type { MergedOrOriginal } from '../../lib/proposals/utils/actionMerging';
import {
  getActionWarningText,
  getApproveButtonLabel,
  getMultiActionSummary,
  getSignificantActions,
} from '../../lib/proposals/utils/actionWarnings';
import { classifyExecutionOutcome } from '../../lib/proposals/utils/classifyExecutionOutcome';
import {
  isGmailSendAction,
  isInformationalProposal,
} from '../../lib/proposals/utils/helpers';
import { showExecutionFailureToast } from '../../lib/proposals/utils/showExecutionFailureToast';
import {
  ApprovalConfirmationModal,
  type EmailRecipients,
} from './ApprovalConfirmationModal';
import { DestructiveWarningModal } from './DestructiveWarningModal';
import {
  useAutoSaveCoordinationOptional,
  useAutoSaveFreezeOwner,
  useExecutionStatus,
} from './shared';

// Platform-aware shortcut symbols
const IS_MAC =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad/.test(navigator.platform);

const SHORTCUT_LABELS = {
  approve: IS_MAC ? '⌘↵' : 'Ctrl+↵',
  reject: IS_MAC ? '⌘⌫' : 'Ctrl+⌫',
} as const;

/**
 * Button content that swaps its label for the key hint while Cmd/Ctrl is held.
 *
 * Both labels render stacked so the button keeps a constant width across the
 * swap. Used by all four decision buttons — Reject/Approve at rest and
 * Cancel/Confirm rejection while the reason form is open — so every advertised
 * binding looks the same.
 */
function ShortcutButtonLabel({
  label,
  shortcut,
  showShortcut,
}: {
  label: ReactNode;
  shortcut: string;
  showShortcut: boolean;
}) {
  return (
    <span className='relative inline-flex items-center justify-center'>
      <span
        className={`transition-opacity duration-100 ${
          showShortcut ? 'opacity-0' : 'opacity-100'
        }`}
      >
        {label}
      </span>
      <span
        aria-hidden='true'
        className={`absolute inset-0 flex items-center justify-center transition-opacity duration-100 ${
          showShortcut ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {shortcut}
      </span>
    </span>
  );
}

interface StickyFooterProps {
  action: ProposalDetail;
  onUpdate: (action: ProposalDetail) => void;
  onComplete: (precomputedNextActionId?: number) => void;
  onSuccess?: () => void;
  /** Resolves the action that follows `currentId` in the list. Reject must
      capture this BEFORE awaiting the reject request: the mutation's list
      invalidation (and the WS proposal event) removes the row mid-await, so
      a post-await lookup no longer finds it and completion falls back to
      the first item instead of advancing. */
  getNextActionId?: (currentId: number) => number | null;
  className?: string;
  /** Selected action IDs for multi-action proposals (undefined = all actions) */
  selectedActionIds?: number[];
  /** Chat input props — renders ConversationInput on the left side of the footer */
  chatMessage?: string;
  onChatChange?: (value: string) => void;
  onChatSubmit?: (e: React.FormEvent) => void;
  isSendingChat?: boolean;
  isProcessingChat?: boolean;
  /** True only while the refine turn kicked off by submitting the gating
   * clarifying answers is running — NOT for ordinary chat turns. Gates the
   * `refining` lock state (see getActionsLockReason). */
  clarifyingRefineInFlight?: boolean;
  /** Merged view of actions; when provided, merged groups count as 1 in the approve label */
  mergedItems?: MergedOrOriginal[];
}

export default function StickyFooter({
  action,
  onUpdate,
  onComplete,
  onSuccess,
  getNextActionId,
  className = '',
  selectedActionIds,
  chatMessage = '',
  onChatChange,
  onChatSubmit,
  isSendingChat = false,
  isProcessingChat = false,
  clarifyingRefineInFlight = false,
  mergedItems,
}: StickyFooterProps) {
  const currentUser = useUser();
  const currentUserEmail: string | undefined = currentUser?.email;

  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [submittingAction, setSubmittingAction] = useState<
    'approve' | 'dismiss' | null
  >(null);
  const isSubmitting = submittingAction !== null;
  const [error, setError] = useState<{
    message: string;
    type: 'polling' | 'execution';
  } | null>(null);
  const [showRejectFeedback, setShowRejectFeedback] = useState(false);
  const rejectButtonRef = useRef<HTMLButtonElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  // Set only when Escape cancels the reject form, so the focus-restoration
  // effect below fires exclusively for that path — never for the Cancel
  // button, a successful reject, or a proposal switch.
  const restoreRejectFocusRef = useRef(false);
  const [showRecipientTooltip, setShowRecipientTooltip] = useState(false);
  const [showDestructiveWarning, setShowDestructiveWarning] = useState(false);
  const [showApprovalConfirmation, setShowApprovalConfirmation] =
    useState(false);
  // Snapshot of actions captured from the query cache right before the
  // confirmation modal opens — reflects autosave edits that may not yet have
  // propagated back through the parent's `action` prop.
  const [confirmActionsSnapshot, setConfirmActionsSnapshot] = useState<
    ProposedAction[] | null
  >(null);
  const [isWaitingForSave, setIsWaitingForSave] = useState(false);
  // Track when user holds Cmd/Ctrl to show shortcut hints
  const [showShortcuts, setShowShortcuts] = useState(false);

  // Use proposal decision hook for reject (approve is dispatched directly
  // through submitApprovalInBackground — see dispatchApprove below)
  const { reject } = useProposalDecision(action.org);

  const queryClient = useQueryClient();
  // Flag the body while this sticky footer is on screen so global CSS lifts
  // toasts above it (desktop only). Ref-counted for concurrent footers.
  useStickyFooterToastLift();
  const approveStartRef = useRef<number>(0);
  // Synchronous re-entrancy guard for the detached approve. `submittingAction`
  // is React state (only reflects on the next render), so a second click or
  // Cmd/Ctrl+Enter fired in the same tick — or in the window before navigation
  // completes — could dispatch a duplicate approve POST. This ref closes the
  // gate synchronously at dispatch; it is lifted only if the approve fails
  // terminally (the proposal rolls back to `pending` and retry must reopen).
  const approveInFlightRef = useRef(false);
  // Synchronous re-entrancy guard for the dismiss/reject decision. Covers ALL
  // three dismiss triggers (footer button, ⌘⌫ keyboard, and the no-op card via
  // NoOpDismissContext) since they all funnel through handleFeedback.
  const dismissInFlightRef = useRef(false);
  // Monotonic token identifying the current approve attempt. A proposal id is
  // NOT enough to identify an attempt: the footer is reused, so a user can
  // approve proposal A, advance, navigate back to A, and retry — a second,
  // legitimately in-flight attempt for the same id. The first attempt's late
  // failure callback would otherwise pass the id check and reopen the second
  // attempt's gate / unfreeze its autosave prematurely. Each dispatch bumps
  // this and captures the value; the failure callback ignores itself unless its
  // captured token still matches the current attempt.
  const approveAttemptRef = useRef(0);
  // Always-current proposal id. A detached approve's failure callback runs
  // after the request settles, by which point the reused footer may have
  // advanced to a different proposal (see the reset effect below). The callback
  // compares this against the id it was dispatched for so a stale failure can't
  // mutate the current proposal's footer state.
  const currentProposalIdRef = useRef(action.id);
  // Monotonic generation for the reload-safety polling path. A proposal id
  // ALONE can't identify a poll attempt: the footer is reused (no key prop), so
  // a user can view proposal A, advance to B, then navigate back to A — which
  // returns currentProposalIdRef to A while an older poll fetch dispatched for
  // the first A view is still in flight. That stale fetch's id check would then
  // pass and apply its terminal response to the *current* A attempt. Each
  // proposal switch bumps this generation; handlePollingComplete captures it at
  // dispatch and ignores its own result once the generation has moved on.
  const pollGenerationRef = useRef(0);
  // Flipped false when this footer instance unmounts, so an in-flight poll
  // fetch that settles after a keyed remount (ProposalDetailPanel key path)
  // doesn't push stale data through the parent's onUpdate.
  const isMountedRef = useRef(true);

  // Holds the reload-safety poll's `retry()` so handlePollingComplete (defined
  // before useExecutionPolling returns it) can re-arm polling when a poll
  // reports the in-progress `pending` state. Populated by an effect below.
  const retryPollingRef = useRef<(() => void) | null>(null);

  // Get notifyProposalUpdated from context to trigger MaterialsList refresh
  const { notifyProposalUpdated } = useProposalSidebar();

  // Autosave coordination - gracefully handles missing provider
  const saveCoordination = useAutoSaveCoordinationOptional();
  // Owns THIS footer's freeze-owner token for its most recent decision. The
  // AutoSaveCoordinationProvider is mounted once, unkeyed, and reused across
  // proposals, and the freeze is reference-counted per owner: freeze() mints a
  // distinct token, and release(token) releases ONLY that token's hold (the
  // freeze lifts once the last owner releases). Each decision captures its own
  // token in a local const and releases exactly that on failure, so a stale
  // approve FAILURE for proposal A can never lift a LATER proposal B's reject
  // freeze — B's reject holds a different token. currentToken holds the latest
  // footer token so navigation cleanup can release the freeze a SUCCESSFUL
  // decision intentionally left held, without clobbering a card's "I don't have
  // this information" exit freeze (a separate owner with its own token,
  // released by the card itself).
  const decisionFreeze = useAutoSaveFreezeOwner(saveCoordination);

  // No-op dismiss registration - gracefully handles missing provider
  const noOpDismiss = useNoOpDismissOptional();

  const [hasPendingOrSaving, setHasPendingOrSaving] = useState(false);
  // A registered editor whose retries have exhausted mirrors `error` (not
  // `pending`/`saving`), so it does NOT show up in `hasPendingOrSaving`. We
  // still must flush-and-block on approval for it, otherwise the proposal
  // executes with the stale server payload the failed save never overwrote.
  const [hasError, setHasError] = useState(false);
  // Tracks content changes to re-render when live editor content updates (e.g., recipient edits)
  const [contentVersion, setContentVersion] = useState(0);

  // Structured clarifying questions exist on this proposal. The questions
  // themselves render as a content-region card (see ProposalDetailPanel /
  // ClarifyingQuestionsPanel — task 4); the footer only adapts its chat
  // placeholder.
  const isStructured = !!action.clarifying_questions?.questions?.length;
  const isPending = action.resolution === 'pending';
  // Live-derived: every action on the proposal is a no-op against the
  // current CRM record (see ProposalSelectionContext's noOpActionIds),
  // rather than the proposal-time snapshot.
  const { noOpActionIds } = useProposalSelection();
  const allActionsNoOp = useMemo(() => {
    if (action.actions.length === 0) {
      return false;
    }
    const noOpIdSet = new Set(noOpActionIds);
    return action.actions.every(a => noOpIdSet.has(a.id));
  }, [action.actions, noOpActionIds]);
  // Custom-UI proposals have actions.length === 0, so they never qualify.
  const noOpDismissMode = allActionsNoOp && isPending;

  // Timestamp of the commit-click that started the current reject decision,
  // used to compute click→advance duration for the proposal_decision timing
  // beacon. The approve path is timed separately via approveStartRef in
  // dispatchApprove, which also stamps this ref so submitApprovalInBackground
  // can emit the executing/sync/retried variants once the request settles.
  const decisionStartRef = useRef<number>(0);

  // Check if the proposal is in executing state
  const isExecuting = action.resolution === 'executing';

  // Poll for execution completion when in executing state. Navigation and
  // success toasts for the approve flow are owned by the execution tracker
  // (see approvalExecutionTracker), which watches the same executing→resolved
  // transition via WebSocket. This polling hook remains as a reload-only
  // safety net so a page refresh mid-execution still resolves inline — it
  // refetches, updates the UI, AND independently classifies/surfaces
  // execution failures (proposal-level or partial), since the tracker may
  // never have been running in this tab to do it.
  const handlePollingComplete = useCallback(
    async (_status: ProposalExecutionStatus) => {
      // Capture the proposal this poll was dispatched for. The footer is reused
      // across proposals (no key prop — see the reset effect below), so during
      // the await the user may advance to a different proposal. The generation
      // pins the specific view: an A→B→A navigation returns the id to A but
      // bumps the generation twice, so this fetch is still recognized as stale.
      const polledProposalId = action.id;
      const polledProposalTitle = action.title;
      const polledGeneration = pollGenerationRef.current;

      // Fetch fresh proposal data directly via API — NOT through React Query.
      let freshData: ProposalDetail | undefined;
      try {
        const client = createProposalClient(action.org);
        freshData = await client.get(polledProposalId);
      } catch (err) {
        logger.warn('[ExecutionPolling] failed to fetch fresh proposal data', {
          component: 'StickyFooter',
          proposalId: polledProposalId,
          error: err,
        });
        freshData = undefined;
      }

      // If the footer advanced to another proposal (or unmounted) while this
      // fetch was in flight, applying freshData through the now-current footer
      // would display/cache the previous proposal as the current one. Skip
      // every footer-scoped mutation (onUpdate/notifyProposalUpdated/setError)
      // unless this is still the same live view — matched on generation, not
      // just id, so an A→B→A round trip doesn't look current (see
      // pollGenerationRef). The id-scoped toasts below still fire so a late
      // outcome for the left-behind proposal isn't lost.
      const stillCurrent =
        isMountedRef.current &&
        currentProposalIdRef.current === polledProposalId &&
        pollGenerationRef.current === polledGeneration;

      if (!freshData) {
        if (stillCurrent) {
          setError({
            message:
              "Couldn't load the updated action. Refresh the page and try again.",
            type: 'polling',
          });
        } else {
          // The user has moved on, but the background refresh for the proposal
          // they left still failed — without this the failure would vanish
          // silently. Surface an id-scoped recovery toast with a View action
          // so they can reopen that proposal and check its status.
          showExecutionFailureToast({
            orgId: action.org,
            proposalId: polledProposalId,
            proposalTitle: polledProposalTitle,
            message:
              'Could not confirm the execution result. Open the action to check its status.',
          });
        }
        return;
      }
      // `pending` is an in-progress state, not a terminal one — the approval
      // tracker treats pending and executing alike as non-terminal. A poll can
      // briefly report `pending` (a stale/out-of-order status, or a reset for
      // retry) while execution is still unresolved. useExecutionPolling stops on
      // any non-`executing` status, so this reload-safety path must re-arm
      // polling for `pending` rather than run the non-approved-terminal failure
      // handling below (which would fire a false failure toast and leave the
      // proposal stuck). Don't push the pending status through onUpdate: keep the
      // UI in its executing state so polling keeps watching until a genuine
      // terminal resolution arrives (identical stuck-execution risk profile to a
      // never-resolving `executing`, which this hook already tolerates).
      if (freshData.resolution === 'pending') {
        logger.info('[ExecutionPolling] pending status — continuing to poll', {
          component: 'StickyFooter',
          proposalId: freshData.id,
        });
        if (stillCurrent) {
          retryPollingRef.current?.();
        }
        return;
      }

      if (stillCurrent) {
        onUpdate(freshData);
        notifyProposalUpdated();
      }

      const outcome = classifyExecutionOutcome(freshData);
      // A proposal that resolved to anything other than `approved` (rejected,
      // withdrawn, failed) is NOT a successful execution — even when its detail
      // carries no execution-level error (so classifyExecutionOutcome, which
      // only inspects execution_result, reports 'success'). Treat any non-
      // approved resolution as a failure so the recovery toast fires and
      // telemetry records the true outcome, matching the `resolution ===
      // 'approved'` gate the approve tracker uses.
      const isFailure =
        outcome.status === 'failure' || freshData.resolution !== 'approved';
      track('proposal_execution_outcome', {
        proposal_id: freshData.id,
        outcome: isFailure ? 'failure' : 'success',
      });
      if (isFailure) {
        const message =
          outcome.status === 'failure'
            ? outcome.message
            : 'Could not confirm this action was approved. Open the action to check its status.';
        logger.warn('[ExecutionPolling] execution failed', {
          component: 'StickyFooter',
          proposalId: freshData.id,
          message,
        });
        showExecutionFailureToast({
          orgId: action.org,
          proposalId: freshData.id,
          proposalTitle: freshData.title,
          message,
        });
      }
    },
    [action.org, action.id, action.title, onUpdate, notifyProposalUpdated]
  );

  const { isPolling, retry } = useExecutionPolling({
    orgId: action.org,
    proposalId: action.id,
    enabled: isExecuting,
    interval: 1500,
    onComplete: handlePollingComplete,
    onError: () => {
      setError({
        message:
          "We couldn't check the execution status. The actions may still be running. Try again.",
        type: 'polling',
      });
    },
  });

  // Keep the ref pointing at the current `retry` so handlePollingComplete (which
  // is defined above, before `retry` exists) can re-arm polling on a `pending`
  // status. `retry` is a fresh closure each render, so sync via a ref.
  useEffect(() => {
    retryPollingRef.current = retry;
  }, [retry]);

  // Diagnostic: trace the executing→resolved transition as this surface
  // observes it (or fails to).
  useEffect(() => {
    logger.info('[ExecutionPolling] sticky footer state', {
      component: 'proposals',
      proposalId: action.id,
      resolution: action.resolution,
      isExecuting,
      isPolling,
    });
  }, [action.id, action.resolution, isExecuting, isPolling]);

  // Reset the approve submit lock when switching proposals. ProposalSidebar
  // reuses a single StickyFooter instance across proposals (no key prop), so
  // without this the just-approved proposal's in-flight guard and loading state
  // would carry into the next proposal's approve button. Also refresh the
  // current-proposal id so a previous proposal's late approve failure can tell
  // it has since advanced (see dispatchApprove's onFailure).
  useEffect(() => {
    // Read the PREVIOUS proposal id (set on the last run / at mount) before we
    // overwrite it below, so we can tell a real navigation from the initial
    // mount.
    const navigatedAway = currentProposalIdRef.current !== action.id;
    currentProposalIdRef.current = action.id;
    // Invalidate any poll fetch dispatched for the previous proposal view, even
    // if the id later returns to the same value (A→B→A). See pollGenerationRef.
    pollGenerationRef.current += 1;
    approveInFlightRef.current = false;
    dismissInFlightRef.current = false;
    setSubmittingAction(null);
    // Close any reject reason form left open by the PREVIOUS proposal. The
    // footer is reused unkeyed across proposals, so a form left open (with a
    // typed reason) would otherwise bleed onto the next proposal, and
    // Cmd/Ctrl+Enter would reject that next proposal with the prior one's
    // reason. Safe no-op at mount — the form starts closed.
    setShowRejectFeedback(false);
    setFeedbackMessage('');
    restoreRejectFocusRef.current = false;
    // On navigation, release the freeze THIS footer's previous decision left
    // armed. A SUCCESSFUL approve/reject intentionally never lifts the freeze
    // (only the failure path does — see dispatchApprove.onFailure /
    // handleFeedback), so without this the shared, unkeyed
    // AutoSaveCoordinationProvider stays frozen and the NEXT proposal in the
    // reused sidebar can never persist edits. Release ONLY the footer's own
    // token, not every owner: a card's "I don't have this information" exit may
    // hold the shared freeze at the same time, and a tokenless clear-all would
    // re-enable autosave while that exit is still in flight. The card releases
    // its own token via its own navigation guard. Gated on an actual id change
    // so the initial mount doesn't spuriously unfreeze.
    if (navigatedAway) {
      decisionFreeze.release(decisionFreeze.currentToken.current);
    }
  }, [action.id, decisionFreeze]);

  // Mark the footer unmounted so a poll fetch that settles after unmount (keyed
  // remount) is treated as stale and skips onUpdate/setError. Kept separate
  // from the reset effect above so it only runs on the true unmount.
  // Re-assert mounted on setup so React Strict Mode's setup→cleanup→setup cycle
  // (which would otherwise leave the guard false after the simulated cleanup)
  // restores it — without this, poll completions get wrongly discarded as stale.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Subscribe to save state changes
  useEffect(() => {
    if (!saveCoordination) {
      return;
    }
    return saveCoordination.subscribe(
      ({ hasPendingOrSaving, hasError, contentVersion }) => {
        setHasPendingOrSaving(hasPendingOrSaving);
        setHasError(hasError);
        setContentVersion(contentVersion);
      }
    );
  }, [saveCoordination]);

  // Detect when user holds Cmd/Ctrl to show shortcut hints on buttons
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey) {
        setShowShortcuts(true);
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (!e.metaKey && !e.ctrlKey) {
        setShowShortcuts(false);
      }
    };
    // Also hide when window loses focus
    const handleBlur = () => setShowShortcuts(false);

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  // Execution status — THE shared derivation (shared/useExecutionStatus),
  // also consumed by ExecutionStatusBanners in the content region so the
  // banner a user reads and the button gating it explains cannot disagree.
  // The full semantics (retryability, indeterminate, stale verdicts) are
  // documented on the hook.
  const {
    primaryAction,
    isEmailAction,
    actionIsIndeterminate,
    actionAlreadyExecuted,
    actionExecutionFailed,
    actionPermanentlyFailed,
  } = useExecutionStatus(action, selectedActionIds);

  const submitBlockedReason = useMemo(() => {
    const reason = primaryAction?.submit_blocked_reason;
    return reason?.trim() ? reason : null;
  }, [primaryAction]);

  // Non-null while a gating clarifying question is unanswered or its answers
  // are still being applied (refine turn running) — see clarifyingGating.ts.
  // Takes precedence over submitBlockedReason in the approveBlockedBy chain
  // below (Decision 2).
  const lockReason = useMemo(
    () =>
      getActionsLockReason(
        action.clarifying_questions,
        clarifyingRefineInFlight
      ),
    [action.clarifying_questions, clarifyingRefineInFlight]
  );
  // Latest lock reason for handleApproveClick's post-flush re-check: the flush
  // awaits, and a gating submission can engage the lock mid-await. The callback
  // closes over a stale lockReason, so read the live value through this ref.
  const lockReasonRef = useLatestRef(lockReason);

  // Memoize significant actions for warning text
  // Filter by selectedActionIds to only show warnings for selected significant actions

  const significantActions = useMemo(
    () => getSignificantActions(action, selectedActionIds),
    [action, selectedActionIds]
  );

  const multiActionSummary = useMemo(
    () => getMultiActionSummary(action, selectedActionIds),
    [action, selectedActionIds]
  );

  const dynamicButtonLabel = useMemo(
    () =>
      getApproveButtonLabel(
        action.actions,
        selectedActionIds,
        mergedItems,
        currentUserEmail
      ),
    [action.actions, selectedActionIds, mergedItems, currentUserEmail]
  );

  // Check for destructive CRM actions (delete, merge)
  const destructiveActions = useMemo(
    () => action.actions.filter(a => isDestructiveCrmPayload(a.payload)),
    [action.actions]
  );
  const hasDestructiveActions = destructiveActions.length > 0;

  const handleRejectClick = () => {
    if (isOnboardingAction) {
      // Onboarding actions skip feedback - reject immediately
      decisionStartRef.current = performance.now();
      handleFeedback('dismiss');
      return;
    }
    if (!showRejectFeedback) {
      // First click - show feedback form
      setShowRejectFeedback(true);
    } else {
      // Second click or form submission - submit dismissal
      decisionStartRef.current = performance.now();
      handleFeedback('dismiss');
    }
  };

  const handleFeedback = async (feedbackType: string) => {
    if (dismissInFlightRef.current) {
      return;
    }
    dismissInFlightRef.current = true;
    setSubmittingAction('dismiss');
    setError(null);
    // Capture the next action NOW, before the reject request commits — the
    // reject mutation's list invalidation (and the WS proposal event) removes
    // this row from the list mid-await, after which the completion handler's
    // own lookup can't find it and falls back to auto-selecting the FIRST
    // item. (The approve path captures its next id pre-dispatch the same way.)
    const nextActionId = getNextActionId?.(action.id) ?? null;
    // Declared before the try so the catch can gate the unfreeze on it (a `try`-
    // scoped const would not be visible in the catch).
    let freezeToken = 0;
    try {
      // 'reject' or 'dismiss' - both reject the proposal.
      // Point of no return: the proposal is about to leave `pending`, after
      // which the backend rejects every update_payload (UPDATE_FAILED).
      // Freeze autosave so no debounce timer armed by a post-flush content
      // reset (editor remount re-emission) fires after the decision starts.
      // Unfrozen only if the decision fails (catch below). Capture this
      // reject's own provider token so a stale approve failure for a different
      // proposal can't release this reject's freeze out from under it — that
      // failure releases a different token.
      freezeToken = decisionFreeze.freeze();
      const updatedProposal = await reject(
        action.id,
        feedbackMessage || `Action: ${feedbackType}`
      );

      // onUpdate/notifyProposalUpdated target proposal-scoped cache and global
      // state, not this footer instance's local UI — safe to run regardless of
      // what's currently displayed, so they stay unconditional.
      onUpdate(updatedProposal);

      // Notify other components (e.g., MaterialsList) that a proposal was updated
      notifyProposalUpdated();

      // The footer is reused unkeyed across proposals (see the reset effect
      // above), so by the time this await resolves the user may have already
      // navigated to a different proposal. Applying the local UI reset or
      // advancing navigation here would clobber that DIFFERENT proposal's
      // reject-feedback state and hijack its view via a stale nextActionId —
      // mirrors dispatchApprove's onFailure id guard above.
      if (currentProposalIdRef.current !== action.id) {
        return;
      }

      // Reset reject feedback UI
      cancelRejectForm();

      // Complete if proposal is now resolved
      if (
        ['approved', 'rejected', 'failed', 'withdrawn'].includes(
          updatedProposal.resolution
        )
      ) {
        // For rejections, always navigate to next (no success popup)
        // Success popup is only shown for approvals
        trackTiming(PROPOSAL_DECISION_TIMING, decisionStartRef.current, {
          decision: 'reject',
          resolution_path: 'sync',
        });
        onComplete(nextActionId ?? undefined);
      }
    } catch (err) {
      // The decision failed terminally — the proposal is still pending, so
      // lift the autosave freeze to let the user edit and retry. Release ONLY
      // this reject's own token: a later decision (approve or reject) that
      // re-froze holds a different token, and a card exit holding the shared
      // freeze holds its own — releasing our token can never unfreeze either
      // of them prematurely.
      decisionFreeze.release(freezeToken);

      const message = getUserFriendlyMessage(err);
      if (isProposalAPIError(err) && err.statusCode === 409) {
        // Stale data — show as toast (dismissable, less alarming)
        toast.error(message);
      } else {
        // Other errors — show as inline error banner
        setError({ message, type: 'execution' });
      }
    } finally {
      setSubmittingAction(null);
      dismissInFlightRef.current = false;
    }
  };

  // Check if this is a calendar event with attendees (other people invited)
  const isCalendarWithAttendees = useMemo(() => {
    const candidateActions =
      selectedActionIds === undefined
        ? action.actions
        : action.actions.filter(a => selectedActionIds.includes(a.id));
    return candidateActions.some(a => {
      if (!isCalendarEventCreatePayload(a.payload)) {
        return false;
      }
      const { attendees } = (a.payload as CalendarEventCreatePayload)
        .action_params;
      return attendees && attendees.length > 0;
    });
  }, [action.actions, selectedActionIds]);

  // Whether to show the approval confirmation modal before executing
  const needsApprovalConfirmation = isEmailAction || isCalendarWithAttendees;

  // Check if this is an informational-only proposal (no executable actions)
  const isInformational = isInformationalProposal(action);

  // Check if proposal contains only external actions (no email, calendar, CRM actions)
  const isExternalActionOnly = useMemo(() => {
    if (!action.actions || action.actions.length === 0) {
      return false;
    }
    return action.actions.every(
      a => a.proposed_action_type_name === 'external_action'
    );
  }, [action.actions]);

  // Check if proposal contains an onboarding action (team invite)
  const isOnboardingAction = useMemo(() => {
    if (!action.actions || action.actions.length === 0) {
      return false;
    }
    return action.actions.some(
      a => a.proposed_action_type_name === 'onboarding_action'
    );
  }, [action.actions]);

  // The deal-pipeline-template proposal is a required onboarding step: a deal
  // can't exist without a pipeline, so this proposal is NOT skippable — it
  // renders no reject/skip affordance (and the keyboard dismiss is gated off),
  // mirroring isPipelineCustomization / isReviewPermissions below.
  const isPipelineTemplate = useMemo(() => {
    if (!action.actions || action.actions.length === 0) {
      return false;
    }
    return action.actions.some(
      a => a.proposed_action_type_name === 'create_pipeline'
    );
  }, [action.actions]);

  // Check if this is the pipeline customization action (no skip button for this)
  const isPipelineCustomization = useMemo(() => {
    if (!action.actions || action.actions.length === 0) {
      return false;
    }
    return action.actions.some(a => {
      const payload = a.payload as { action_params?: { action_key?: string } };
      return payload?.action_params?.action_key === 'customize_pipeline';
    });
  }, [action.actions]);

  // Check if this is the review permissions action (no skip button for this)
  const isReviewPermissions = useMemo(() => {
    if (!action.actions || action.actions.length === 0) {
      return false;
    }
    return action.actions.some(a => {
      const payload = a.payload as { action_params?: { action_key?: string } };
      return payload?.action_params?.action_key === 'review_permissions';
    });
  }, [action.actions]);

  // When the primary action has permanently failed (executed, non-retryable,
  // verdict not stale), the footer collapses to a single one-click Dismiss:
  // Approve can't help and Reject's "why?" form is pointless. Gated off the
  // non-skippable types so it never introduces a dismiss affordance where none
  // exists today. Keying off actionPermanentlyFailed makes deselecting the
  // failed action (verdict goes stale) revert to the normal Approve/Reject pair.
  const showPermanentlyFailedDismiss =
    actionPermanentlyFailed &&
    !isPipelineCustomization &&
    !isReviewPermissions &&
    !isPipelineTemplate;

  // Fires the approve request detached from this component and advances the
  // user immediately — execution is watched by approvalExecutionTracker, not
  // by this component sticking around. See submitApprovalInBackground.
  const dispatchApprove = useCallback(() => {
    // Re-entrancy guard: a second click/shortcut, or one fired before the
    // advance completes, must not dispatch a duplicate approve POST. Close the
    // gate synchronously before any async work.
    if (approveInFlightRef.current) {
      return;
    }
    approveInFlightRef.current = true;
    // Stamp this dispatch so its failure callback can tell whether it is still
    // the active attempt when it settles (see onFailure below).
    const approveAttempt = ++approveAttemptRef.current;
    // Mark approval as submitting so the approve button shows its loading state
    // and the button/keyboard-shortcut gates (which read `isSubmitting`) stay
    // closed until the user advances or the approve fails. The reused
    // StickyFooter instance resets this on `action.id` change (see effect
    // below), so it never leaks into the next proposal's footer.
    setSubmittingAction('approve');

    // Collect live content NOW — after advancing, the editors unmount and
    // collectAllContent() would return [].
    const liveContent = saveCoordination?.collectAllContent() ?? [];

    // Point of no return: the proposal is about to leave `pending`, after
    // which the backend rejects every update_payload (UPDATE_FAILED). Capture
    // this approve's own provider token so its late failure releases only its
    // own hold — a newer decision (approve or reject) that re-froze holds a
    // different token and stays frozen.
    const freezeToken = decisionFreeze.freeze();

    const suppressSuccessToast =
      isOnboardingAction || isPipelineCustomization || isReviewPermissions;

    // Stamp the decision-timing measurement here — immediately before dispatch
    // — rather than in handleApproveClick, so `proposal_decision_timing`
    // measures approve-initiation-to-advance latency and excludes any
    // DestructiveWarningModal/ApprovalConfirmationModal user dwell time for
    // proposals that require confirmation before reaching this point.
    approveStartRef.current = Date.now();
    // Same rationale, but performance.now()-based: submitApprovalInBackground
    // uses this with trackTiming (which measures elapsed via performance.now())
    // once the detached request actually settles (executing/sync/retried).
    decisionStartRef.current = performance.now();

    void submitApprovalInBackground({
      queryClient,
      orgId: action.org,
      proposalId: action.id,
      localSelectedIds: selectedActionIds,
      liveContent,
      suppressSuccessToast,
      decisionStartTime: decisionStartRef.current,
      // The detached approve froze autosave above. If it fails terminally the
      // proposal rolls back to `pending`, so lift the freeze — otherwise a
      // reused sidebar provider stays frozen and later edits never persist.
      // Also reopen the approve gate (ref + submitting state) so the user can
      // retry the still-pending proposal.
      //
      // decisionFreeze.release() is passed this approve's own provider token,
      // so it is NOT id-gated. Navigation via onSuccess()/onComplete() below happens
      // synchronously right after dispatch — well before the network request can
      // fail — so by the time onFailure runs, currentProposalIdRef has almost
      // always already advanced past action.id, and an id-gated unfreeze would
      // never run: the freeze would stay stuck for every subsequent proposal in
      // the session (AutoSaveCoordinationProvider is mounted once, unkeyed, and
      // reused across proposals). Releasing this approve's own token is safe
      // regardless of navigation: the provider freeze is reference-counted, so a
      // *newer* decision (a later approve, or a reject on this or any later
      // proposal) holds a DIFFERENT token and stays frozen, and a card exit
      // holding the shared freeze holds its own token — this release touches
      // neither. If this approve succeeded, its token was already cleared by the
      // navigation cleanup and this release no-ops.
      //
      // approveInFlightRef/setSubmittingAction stay id+attempt-gated: they're
      // UI/re-entrancy state for the CURRENT proposal's footer, and must not
      // be touched by a stale failure for a proposal the user has already
      // left.
      onFailure: () => {
        decisionFreeze.release(freezeToken);
        if (
          currentProposalIdRef.current !== action.id ||
          approveAttemptRef.current !== approveAttempt
        ) {
          return;
        }
        approveInFlightRef.current = false;
        setSubmittingAction(null);
      },
      // Best-effort: the editor may have unmounted by the time this fires
      // (StickyFooter advances synchronously after dispatch), so resync is
      // a no-op if no editors are registered.
      onStaleActions: () => saveCoordination?.resyncFromServer(),
    });

    if (suppressSuccessToast) {
      // Same pre-mutation capture as the reject path: resolve the next action
      // while the list still contains this row (dispatch is detached, so the
      // list is untouched at this point — this is same-tick with dispatch).
      onComplete(getNextActionId?.(action.id) ?? undefined);
    } else {
      onSuccess?.();
    }

    // Measure AFTER the advance callbacks — duration now spans from approve
    // initiation (approveStartRef stamped above) to UI advance, capturing the
    // real user-visible latency rather than just the synchronous dispatch
    // overhead (~0 ms).
    track('proposal_decision_timing', {
      org_id: action.org,
      proposal_id: action.id,
      decision: 'approve',
      duration_ms: Date.now() - approveStartRef.current,
    });
  }, [
    action.id,
    action.org,
    selectedActionIds,
    saveCoordination,
    decisionFreeze,
    isOnboardingAction,
    isPipelineCustomization,
    isReviewPermissions,
    onSuccess,
    onComplete,
    getNextActionId,
    queryClient,
  ]);

  // Handle approve click - show warning modal if destructive actions present
  const handleApproveClick = useCallback(async () => {
    // Never approve while the gating lock is engaged. The button/shortcut gate
    // on entry, but a gating submission can engage the lock while this handler
    // awaits the autosave flush below — so re-check the live lock both here and
    // after the flush, before any confirmation/dispatch path runs.
    if (lockReasonRef.current !== null) {
      return;
    }
    setIsWaitingForSave(true);

    // Wait for any pending autosaves before proceeding. `hasError` is included
    // so an editor whose retries exhausted (mirrored as `error`, absent from
    // `hasPendingOrSaving`) still forces a flush — its flush rejects, blocking
    // approval rather than letting the proposal execute with stale data.
    if (saveCoordination && (hasPendingOrSaving || hasError)) {
      logger.debug('Waiting for autosave to complete before approval...', {
        component: 'StickyFooter',
      });
      const startTime = Date.now();
      const { success, errors } = await saveCoordination.flushAllAndWait();
      const duration = Date.now() - startTime;
      logger.debug('Autosave flush completed', {
        component: 'StickyFooter',
        success,
        errorCount: errors.length,
        durationMs: duration,
      });

      if (!success) {
        setIsWaitingForSave(false);
        setError({
          message: 'Some changes could not be saved. Please try again.',
          type: 'execution',
        });
        return;
      }
    }

    setIsWaitingForSave(false);

    // Re-check after the awaited flush: if a gating submission engaged the lock
    // while we were flushing, abort before opening any confirmation or
    // dispatching approval — an approval must never execute during refinement.
    if (lockReasonRef.current !== null) {
      return;
    }

    // Capture a fresh snapshot of actions from the query cache. After
    // flushAllAndWait() the autosave mutations have completed and the cache
    // holds the latest server data — snapshotting here ensures the
    // confirmation modal reflects autosave edits rather than the
    // possibly-stale `action.actions` prop.
    const freshDetail = queryClient.getQueryData<ProposalDetail>(
      proposalQueryKeys.detail(action.org, action.id)
    );
    setConfirmActionsSnapshot(freshDetail?.actions ?? null);

    // Destructive warning first, then confirmation for emails/calendar-with-attendees, then execute
    if (hasDestructiveActions) {
      setShowDestructiveWarning(true);
    } else if (needsApprovalConfirmation) {
      setShowApprovalConfirmation(true);
    } else {
      dispatchApprove();
    }
  }, [
    saveCoordination,
    hasPendingOrSaving,
    hasError,
    hasDestructiveActions,
    needsApprovalConfirmation,
    queryClient,
    action.org,
    action.id,
    dispatchApprove,
    lockReasonRef,
  ]);

  // Handle confirmation from destructive warning modal
  const handleDestructiveConfirm = useCallback(() => {
    setShowDestructiveWarning(false);
    // A gating submission can engage the lock while this modal sits open; a
    // confirmation click must never open a follow-up modal or dispatch approval
    // during refinement, so re-check the live lock (mirrors handleApproveClick).
    if (lockReasonRef.current !== null) {
      return;
    }
    if (needsApprovalConfirmation) {
      setShowApprovalConfirmation(true);
    } else {
      dispatchApprove();
    }
  }, [needsApprovalConfirmation, dispatchApprove, lockReasonRef]);

  // Handle final confirmation from approval confirmation modal
  const handleApprovalConfirm = useCallback(() => {
    setShowApprovalConfirmation(false);
    setConfirmActionsSnapshot(null);
    // Re-check the live lock: a gating submission can engage it while this modal
    // is open, and confirming must never dispatch approval during refinement.
    if (lockReasonRef.current !== null) {
      return;
    }
    dispatchApprove();
  }, [dispatchApprove, lockReasonRef]);

  // Log the raw execution failure detail server-side text; only static,
  // pre-written copy is ever rendered to the user (see failed-execution
  // warning below).
  useEffect(() => {
    if (!actionExecutionFailed) {
      return;
    }
    const executionError = primaryAction?.execution_result?.error;
    const validationErrors = primaryAction?.execution_result?.validation_errors;
    if (executionError) {
      logger.error('[StickyFooter] action execution failed', undefined, {
        component: 'proposals',
        proposalId: action.id,
        error: executionError,
      });
    }
    if (validationErrors?.length) {
      logger.error(
        '[StickyFooter] action execution validation errors',
        undefined,
        {
          component: 'proposals',
          proposalId: action.id,
          validationErrors: validationErrors.map(err => ({
            entity_type: err.entity_type,
            event_type: err.event_type,
            message: err.message,
          })),
        }
      );
    }
  }, [actionExecutionFailed, primaryAction?.execution_result, action.id]);

  // Mirror handleFeedback into a ref for stableNoOpDismiss (see useLatestRef
  // docstring: the callback is handed to another hook and to a JSX prop, so
  // useEffectEvent is not applicable per REACT_19_PATTERNS § 4.1).
  const handleFeedbackRef = useLatestRef(handleFeedback);

  // Stable wrapper around the always-current handleFeedbackRef for the
  // NoOpDismissContext registration below. handleFeedbackRef identity is
  // stable (RefObject), so stableNoOpDismiss never changes across renders
  // (required so the registration effect doesn't depend on a function that
  // changes every render).
  const stableNoOpDismiss = useCallback(() => {
    decisionStartRef.current = performance.now();
    handleFeedbackRef.current('dismiss');
  }, [handleFeedbackRef]);

  // Effect Event for Cmd/Ctrl+Enter shortcut: inlines the approve guard and
  // calls handleApproveClick. Closes over the latest render's state without
  // needing refs (REACT_19_PATTERNS § 3.3 / useEffectEvent).
  const handleApproveShortcut = useEffectEvent((e: KeyboardEvent) => {
    const noActionsSelected =
      selectedActionIds !== undefined && selectedActionIds.length === 0;
    // Don't trigger if already submitting, executing, polling, showing
    // reject feedback, action already executed, permanently failed,
    // indeterminate (sticky-skip — must be resolved explicitly),
    // mid-flush, no actions selected, or the server has set a blocked
    // reason. An autosave merely in flight (hasPendingOrSaving) does NOT
    // block: handleApproveClick flushes pending saves before submitting.
    if (
      isSubmitting ||
      isExecuting ||
      isPolling ||
      showRejectFeedback ||
      actionAlreadyExecuted ||
      actionPermanentlyFailed ||
      actionIsIndeterminate ||
      isWaitingForSave ||
      submitBlockedReason !== null ||
      lockReason !== null ||
      noActionsSelected
    ) {
      return;
    }
    e.preventDefault();
    handleApproveClick();
  });

  // Effect Event for Cmd/Ctrl+Enter: at rest, delegates to the approve path;
  // with the reason form open, COMMITS the rejection (mirrors "Confirm
  // rejection" — handleRejectClick, isDisabled={isSubmitting}).
  const handleEnterShortcut = useEffectEvent((e: KeyboardEvent) => {
    if (showRejectFeedback) {
      if (isSubmitting) {
        return;
      }
      e.preventDefault();
      handleRejectClick();
      return;
    }
    // At rest: don't trigger from plain input/textarea, but DO allow
    // contentEditable (email editor). Delegate to the approve path, whose
    // gate (incl. the showRejectFeedback early-return above) is preserved
    // intact.
    const target = e.target as HTMLElement | null;
    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement
    ) {
      return;
    }
    handleApproveShortcut(e);
  });

  // Effect Event for Cmd/Ctrl+Backspace shortcut: mirrors the RENDERED
  // decision button (same precedence as the render at ~1465) rather than
  // re-deriving the decision.
  const handleBackspaceShortcut = useEffectEvent((e: KeyboardEvent) => {
    // Reason form open: CANCEL the form, mirroring the Cancel button this key
    // is advertised on (isDisabled={isSubmitting}). Runs before the
    // editable-target guard below because the reason textarea is autoFocused —
    // gating on it would make the advertised binding unreachable in the state
    // it is advertised in.
    //
    // This deliberately overrides macOS delete-to-start-of-line inside the
    // reason textarea. That trade was made knowingly: the binding is printed
    // on the button, and a key hint that does nothing is worse than a lost
    // text-editing default. Escape remains bound to the same cancel.
    if (showRejectFeedback) {
      if (isSubmitting) {
        return;
      }
      e.preventDefault();
      // Return focus to the Reject button, matching the Escape cancel path so
      // the keyboard flow continues without reaching for the mouse.
      restoreRejectFocusRef.current = true;
      cancelRejectForm();
      return;
    }
    // At rest: don't trigger while the user is typing in an editable field
    // (the email editor is contentEditable). Moved here from the window
    // listener so both branches of this key are gated in one place.
    const target = e.target as HTMLElement | null;
    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target?.isContentEditable
    ) {
      return;
    }
    // In-flight or no reject affordance: do nothing (per the issue's AC, all
    // seven flags mean "nothing").
    if (
      isSubmitting ||
      isExecuting ||
      isPolling ||
      isWaitingForSave ||
      isPipelineCustomization ||
      isReviewPermissions ||
      isPipelineTemplate
    ) {
      return;
    }
    e.preventDefault();
    // Mirror the RENDERED decision button (same precedence as the render at
    // ~1465); never re-derive the decision.
    if (noOpDismissMode || showPermanentlyFailedDismiss) {
      // One-click Dismiss button path. Call handleFeedback directly rather
      // than the ref-backed stableNoOpDismiss: this Effect Event closes over
      // the latest handleFeedback, dodging the useLatestRef passive-sync
      // stale-proposal race the old direct call was written to avoid.
      decisionStartRef.current = performance.now();
      handleFeedback('dismiss');
    } else {
      // Two-step Reject button path: handleRejectClick rejects immediately
      // for onboarding actions, otherwise its first invocation opens the
      // reason form and focuses the field.
      handleRejectClick();
    }
  });

  // Register the one-click dismiss handler with NoOpDismissContext so the
  // sibling "Already up to date" empty-state card can trigger it. Depend only
  // on the stable `register` + primitives, NEVER on the handler function
  // itself, to avoid a registration loop. The cleanup handles unmount +
  // leaving no-op mode (unregister -> card fails closed).
  const registerNoOpDismiss = noOpDismiss?.register;
  const dismissInProgress = submittingAction === 'dismiss';
  useEffect(() => {
    if (!registerNoOpDismiss) {
      return;
    }
    if (noOpDismissMode) {
      registerNoOpDismiss({
        dismiss: stableNoOpDismiss,
        isDismissing: dismissInProgress,
      });
      return () => registerNoOpDismiss(null);
    }
    registerNoOpDismiss(null);
    return undefined;
  }, [
    registerNoOpDismiss,
    noOpDismissMode,
    dismissInProgress,
    stableNoOpDismiss,
  ]);

  const cancelRejectForm = useCallback(() => {
    setShowRejectFeedback(false);
    setFeedbackMessage('');
  }, []);

  // Escape cancels the open reason form and stops the host SlideoutMenu from
  // closing. stopPropagation() covers the modal overlay: react-aria's
  // useOverlay Escape handler is a bubble-phase synthetic onKeyDown on an
  // ancestor, so stopping propagation from this descendant means it never runs.
  // preventDefault() covers the non-modal slideout, whose document-level
  // listener explicitly bails on defaultPrevented
  // (frontend/src/lib/base/overlays/slideout-menu-nonmodal.tsx:345).
  // Mirrors InlineEditField.tsx:113-146.
  const handleFooterKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'Escape' || !showRejectFeedback) {
        return; // form closed -> let the host keep its own dismiss
      }
      // A reject request is already in flight: the form's Cancel/Confirm
      // controls and textarea are all disabled (isSubmitting), so Escape must
      // not hide the form out from under the running dismissal either. Leave
      // the key for the host — the form reappears at rest only via the normal
      // reject success/failure path.
      if (isSubmitting) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      restoreRejectFocusRef.current = true;
      cancelRejectForm();
    },
    [showRejectFeedback, isSubmitting, cancelRejectForm]
  );

  // The Reject button remounts when the form closes, so restore focus after
  // re-render. Guarded by restoreRejectFocusRef so this fires ONLY after an
  // Escape cancel — never on the Cancel button, a successful reject, or a
  // proposal switch. Null-guarded because decisionButton can be null for some
  // proposal types.
  useEffect(() => {
    if (!showRejectFeedback && restoreRejectFocusRef.current) {
      restoreRejectFocusRef.current = false;
      if (rejectButtonRef.current) {
        rejectButtonRef.current.focus();
      } else {
        footerRef.current?.focus();
      }
    }
  }, [showRejectFeedback]);

  // Keyboard shortcuts: Cmd+Enter (Mac) or Ctrl+Enter (Windows/Linux) to
  // approve/commit; Cmd+Backspace or Ctrl+Backspace to mirror the rendered
  // decision button
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Cmd+Enter (Mac) or Ctrl+Enter (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        handleEnterShortcut(e);
      }
      // Check for Cmd+Backspace (Mac) or Ctrl+Backspace (Windows/Linux). The
      // editable-target guard lives inside handleBackspaceShortcut, which must
      // see the form-open state first — same shape as the Enter branch above.
      else if ((e.metaKey || e.ctrlKey) && e.key === 'Backspace') {
        handleBackspaceShortcut(e);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // Effect Events (handleEnterShortcut, which internally delegates to
    // handleApproveShortcut; handleBackspaceShortcut) and useLatestRef-backed
    // callbacks (stableNoOpDismiss) are non-reactive by design — they always
    // see the latest values without re-subscribing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get live email recipients from the autosave coordination system.
  // This reflects the user's current edits (e.g., changed "to" field) without waiting
  // for the 2-second autosave debounce + API round-trip to complete.
  const getLiveEmailParams = useCallback(() => {
    if (
      !primaryAction ||
      !isGmailSendAction(primaryAction) ||
      !saveCoordination
    ) {
      return null;
    }
    const liveContent = saveCoordination.collectAllContent();
    const emailContent = liveContent.find(c => c.actionId === primaryAction.id);
    if (!emailContent) {
      return null;
    }
    const actionParams = (
      emailContent.payload as { action_params?: Record<string, unknown> }
    )?.action_params;
    if (!actionParams) {
      return null;
    }
    return actionParams as { to?: string[]; cc?: string[]; bcc?: string[] };
  }, [primaryAction, saveCoordination]);

  // Get email recipients for warning message — prefers live editor content over saved props
  const emailRecipients = useMemo(() => {
    if (!primaryAction || !isGmailSendAction(primaryAction)) {
      return '';
    }

    // Try live content first (reflects unsaved edits in real time)
    const liveParams = getLiveEmailParams();
    const toAddresses =
      liveParams?.to ??
      (primaryAction.payload as GmailSendPayload)?.action_params?.to;
    if (!toAddresses?.length) {
      return '';
    }

    const parts = [];

    // Add TO addresses
    parts.push(toAddresses.join(', '));

    // Add CC addresses if present
    const ccAddresses =
      liveParams?.cc ??
      (primaryAction.payload as GmailSendPayload)?.action_params?.cc;
    if (ccAddresses && ccAddresses.length > 0) {
      parts.push(`CC: ${ccAddresses.join(', ')}`);
    }

    return parts.join('; ');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- contentVersion drives re-computation
  }, [getLiveEmailParams, primaryAction, contentVersion]);

  // Count total recipients for mobile display — prefers live editor content
  const recipientCount = useMemo(() => {
    if (!primaryAction || !isGmailSendAction(primaryAction)) {
      return 0;
    }

    const liveParams = getLiveEmailParams();
    const toCount =
      (
        liveParams?.to ??
        (primaryAction.payload as GmailSendPayload)?.action_params?.to
      )?.length || 0;
    const ccCount =
      (
        liveParams?.cc ??
        (primaryAction.payload as GmailSendPayload)?.action_params?.cc
      )?.length || 0;
    return toCount + ccCount;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- contentVersion drives re-computation
  }, [getLiveEmailParams, primaryAction, contentVersion]);

  // Build live email recipients object for approval confirmation modal
  const liveEmailRecipients = useMemo((): EmailRecipients | null => {
    const liveParams = getLiveEmailParams();
    if (!liveParams) {
      return null;
    }
    return {
      to: liveParams.to ?? [],
      cc: liveParams.cc ?? [],
      bcc: liveParams.bcc ?? [],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- contentVersion drives re-computation
  }, [getLiveEmailParams, contentVersion]);

  // Get actions to show in the approval confirmation modal.
  // Prefers confirmActionsSnapshot (captured from the query cache right before
  // the modal opens, so autosave edits are reflected) over the possibly-stale
  // `action.actions` prop.
  const confirmationActions = useMemo(() => {
    const actionsSource = confirmActionsSnapshot ?? action.actions;
    if (selectedActionIds === undefined) {
      return actionsSource;
    }
    return actionsSource.filter(a => selectedActionIds.includes(a.id));
  }, [action.actions, confirmActionsSnapshot, selectedActionIds]);

  // Approve button label (resolved before render to avoid nested ternaries).
  // Note: we deliberately do NOT swap the label to "Saving..." when an autosave
  // is in flight or while waiting for a flush. The button should look the same
  // all the time; the only visual signal that something's happening is the
  // spinner overlay (isLoading), which only triggers after the user actually
  // invokes the button (submittingAction set, or isWaitingForSave from a
  // freshly-clicked handleApproveClick).
  let approveButtonLabel: string;
  if (isExecuting || isPolling) {
    approveButtonLabel = 'Executing actions…';
  } else if (dynamicButtonLabel) {
    approveButtonLabel = dynamicButtonLabel;
  } else if (isInformational) {
    approveButtonLabel = 'Mark complete';
  } else if (actionAlreadyExecuted && isEmailAction) {
    approveButtonLabel = 'Already sent';
  } else if (isPipelineCustomization) {
    approveButtonLabel = 'Save pipeline';
  } else if (isExternalActionOnly) {
    approveButtonLabel = 'Mark done & continue';
  } else if (isOnboardingAction && primaryAction) {
    const registryEntry = lookupAction(primaryAction);
    approveButtonLabel = registryEntry?.getApproveLabel
      ? registryEntry.getApproveLabel(primaryAction.payload, primaryAction, {
          currentUserEmail,
        })
      : 'Send invitations';
  } else if (primaryAction) {
    // Use registry approve label for single-action proposals
    const registryEntry = lookupAction(primaryAction);
    approveButtonLabel = registryEntry?.getApproveLabel
      ? registryEntry.getApproveLabel(primaryAction.payload, primaryAction, {
          currentUserEmail,
        })
      : 'Approve & continue';
  } else {
    approveButtonLabel = 'Approve & continue';
  }

  // Which condition, if any, is holding Approve disabled.
  //
  // This is the SINGLE source of truth for that: the button's `isDisabled` is
  // `approveBlockedBy !== undefined`, so the diagnostic and the actual disabled
  // state cannot drift apart the way a parallel boolean expression would. The
  // branch order decides only which key wins when several conditions hold at
  // once; the resulting boolean is the same OR of the same terms as before.
  //
  // It surfaces as `data-blocked-by` because "Approve is dead and I can't tell
  // why" is the most-reported confusion on this footer, and none of these
  // conditions are visible from the page or from an e2e run. Note that several
  // are transient by design — `pending-save` clears when a 2s autosave debounce
  // settles — so a value here does not by itself mean something is wrong.
  let approveBlockedBy: string | undefined;
  if (lockReason !== null) {
    approveBlockedBy = 'gating';
  } else if (isSubmitting) {
    approveBlockedBy = 'submitting';
  } else if (isExecuting) {
    approveBlockedBy = 'executing';
  } else if (isPolling) {
    approveBlockedBy = 'polling';
  } else if (actionAlreadyExecuted) {
    approveBlockedBy = 'already-executed';
  } else if (actionPermanentlyFailed) {
    approveBlockedBy = 'permanently-failed';
  } else if (actionIsIndeterminate) {
    // An indeterminate action is sticky-skip on the backend: clicking Approve
    // cannot help, it must be resolved explicitly via the resolve-indeterminate
    // affordance.
    approveBlockedBy = 'indeterminate';
  } else if (isWaitingForSave) {
    approveBlockedBy = 'waiting-for-save';
  } else if (submitBlockedReason !== null) {
    approveBlockedBy = 'server-blocked';
  } else if (
    selectedActionIds !== undefined &&
    selectedActionIds.length === 0
  ) {
    approveBlockedBy = 'no-selection';
  }

  // Tooltip text for the disabled Approve button. Gating takes precedence —
  // matches the approveBlockedBy chain above — so the tooltip reads "Answer
  // the question above to continue" while unanswered and "Applying your
  // answers…" during the refine window; it is not stuck on the answer prompt
  // after submit.
  let approveDisabledTooltip: string | undefined;
  if (lockReason !== null) {
    approveDisabledTooltip = actionsLockMessage(lockReason);
  } else if (submitBlockedReason && !hasPendingOrSaving) {
    approveDisabledTooltip = submitBlockedReason;
  }

  // Reject button label
  let rejectButtonLabel: string;
  if (isInformational) {
    rejectButtonLabel = 'Dismiss';
  } else if (isOnboardingAction) {
    rejectButtonLabel = 'Skip';
  } else {
    rejectButtonLabel = 'Reject';
  }

  // Mobile warning content (computed before render to avoid nested ternaries)
  let mobileWarningContent: ReactNode = null;
  if (!showRejectFeedback && multiActionSummary) {
    mobileWarningContent = (
      <div className='flex sm:hidden mb-2 relative'>
        <Button
          color='link-gray'
          onClick={() => setShowRecipientTooltip(!showRecipientTooltip)}
          className='text-xs text-secondary hover:text-primary gap-1.5'
          iconLeading={InfoCircle}
          iconLeadingClassName='w-3.5 h-3.5 text-utility-brand-600 hover:text-utility-brand-600'
        >
          <span className='underline decoration-dotted'>
            {multiActionSummary.summary}
          </span>
        </Button>
        {showRecipientTooltip && (
          <div className='absolute bottom-full left-0 mb-2 px-3 py-3 bg-primary-solid rounded-lg shadow-lg max-w-xs z-10'>
            <div className='text-xs font-semibold text-white mb-1'>
              Actions:
            </div>
            <ul className='text-xs font-medium text-tooltip-supporting-text list-disc list-inside'>
              {multiActionSummary.tooltipItems.map((item, i) => (
                // eslint-disable-next-line react/no-array-index-key -- Tooltip items are display strings that may repeat; index is the only stable identity
                <li key={i}>{item}</li>
              ))}
            </ul>
            <div className='absolute bottom-0 left-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-primary-solid' />
          </div>
        )}
      </div>
    );
  } else if (!showRejectFeedback && isEmailAction && recipientCount > 0) {
    mobileWarningContent = (
      <div className='flex sm:hidden mb-2 relative'>
        <Button
          color='link-gray'
          onClick={() => setShowRecipientTooltip(!showRecipientTooltip)}
          className='text-xs text-secondary hover:text-primary gap-1.5'
          iconLeading={InfoCircle}
          iconLeadingClassName='w-3.5 h-3.5 text-utility-brand-600 hover:text-utility-brand-600'
        >
          <span className='underline decoration-dotted'>
            Will send to {recipientCount} recipient
            {recipientCount !== 1 ? 's' : ''}
          </span>
        </Button>
        {showRecipientTooltip && emailRecipients && (
          <div className='absolute bottom-full left-0 mb-2 px-3 py-3 bg-primary-solid rounded-lg shadow-lg max-w-xs z-10'>
            <div className='text-xs font-semibold text-white mb-1'>
              Recipients:
            </div>
            <div className='text-xs font-medium text-tooltip-supporting-text break-words'>
              {emailRecipients}
            </div>
            <div className='absolute bottom-0 left-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-primary-solid' />
          </div>
        )}
      </div>
    );
  } else if (
    !showRejectFeedback &&
    significantActions.length === 1 &&
    !isEmailAction
  ) {
    const warning = getActionWarningText(significantActions[0]);
    if (warning) {
      mobileWarningContent = (
        <div className='flex sm:hidden mb-2 relative'>
          <Button
            color='link-gray'
            onClick={() => setShowRecipientTooltip(!showRecipientTooltip)}
            className='text-xs text-secondary hover:text-primary gap-1.5'
            iconLeading={InfoCircle}
            iconLeadingClassName='w-3.5 h-3.5 text-utility-brand-600 hover:text-utility-brand-600'
          >
            <span className='underline decoration-dotted'>
              {warning.condensed}
            </span>
          </Button>
          {showRecipientTooltip && (
            <div className='absolute bottom-full left-0 mb-2 px-3 py-2 bg-primary-solid rounded-lg shadow-lg max-w-xs z-10'>
              <div className='text-xs font-semibold text-white break-words'>
                {warning.tooltip}
              </div>
              <div className='absolute bottom-0 left-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-primary-solid' />
            </div>
          )}
        </div>
      );
    }
  }

  // Left cell: the chat input, or a spacer. The clarifying-questions card
  // renders in the CONTENT region (ProposalDetailPanel — task 4), never here.
  let leftCellContent: ReactNode;
  if (
    !showRejectFeedback &&
    !isOnboardingAction &&
    onChatChange &&
    onChatSubmit
  ) {
    leftCellContent = (
      <div className='flex-1 min-w-0 flex flex-col gap-2'>
        <ConversationInput
          value={chatMessage}
          onChange={onChatChange}
          onSubmit={onChatSubmit}
          placeholder={
            isStructured ? 'Something else?' : 'Ask to update anything'
          }
          loading={isSendingChat}
          isProcessing={isProcessingChat}
          variant='simple'
          sticky={false}
          compactUntilWrap
        />
      </div>
    );
  } else {
    leftCellContent = <div className='hidden sm:block flex-1' />;
  }

  // Compute the decision button before render to avoid a nested ternary
  // (no-nested-ternary): one-click Dismiss when every action is no-op, the
  // existing two-step reject button otherwise (or nothing for proposal types
  // that don't support skip/reject).
  let decisionButton: ReactNode;
  if (noOpDismissMode || showPermanentlyFailedDismiss) {
    decisionButton = (
      <Button
        size='md'
        color='secondary'
        onClick={stableNoOpDismiss}
        isDisabled={isSubmitting}
        isLoading={submittingAction === 'dismiss'}
        data-component='action-button'
        data-instance='dismiss-proposal'
        data-state={submittingAction === 'dismiss' ? 'saving' : undefined}
        data-testid='proposal-noop-dismiss-button'
      >
        Dismiss
      </Button>
    );
  } else if (
    !isPipelineCustomization &&
    !isReviewPermissions &&
    !isPipelineTemplate
  ) {
    decisionButton = (
      <Button
        ref={rejectButtonRef}
        size='md'
        color={isOnboardingAction ? 'secondary' : 'secondary-destructive'}
        onClick={handleRejectClick}
        isDisabled={
          isSubmitting || isExecuting || isPolling || isWaitingForSave
        }
        isLoading={submittingAction === 'dismiss'}
        data-component='action-button'
        data-instance='reject-proposal'
        data-state={submittingAction === 'dismiss' ? 'saving' : undefined}
        data-testid='proposal-reject-button'
      >
        <ShortcutButtonLabel
          label={rejectButtonLabel}
          shortcut={SHORTCUT_LABELS.reject}
          showShortcut={showShortcuts && !isSubmitting}
        />
      </Button>
    );
  } else {
    decisionButton = null;
  }

  return (
    <div
      ref={footerRef}
      className={`border-t border-primary bg-primary transition-opacity duration-200 pb-[env(safe-area-inset-bottom)] ${className}`}
      data-testid='sticky-footer-root'
      onKeyDown={handleFooterKeyDown}
      tabIndex={-1}
    >
      <div className='px-3 sm:px-4 lg:px-6 py-3'>
        {/* The already-sent and failed-execution banners moved to the
            content-region banner stack (ExecutionStatusBanners in
            ProposalDetailPanel — task 4 part 2a), fed by the same
            useExecutionStatus derivation that gates the buttons below. */}

        {/* Error Display */}
        {error && (
          <div className='flex items-center gap-3 p-3 rounded-lg bg-utility-error-50 border border-utility-error-200 text-utility-error-800 mb-4'>
            <svg
              className='w-6 h-6 text-utility-error-600 flex-shrink-0'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
            <span className='flex-1 text-sm'>{error.message}</span>
            <div className='flex gap-2'>
              {error.type === 'polling' && (
                <Button
                  color='primary'
                  size='sm'
                  onClick={() => {
                    setError(null);
                    retry();
                  }}
                  disabled={isPolling}
                >
                  Retry status check
                </Button>
              )}
              <Button
                color='secondary'
                size='sm'
                onClick={() => setError(null)}
              >
                Dismiss
              </Button>
            </div>
          </div>
        )}

        {/* Feedback Textarea - Only shown when rejecting */}
        {showRejectFeedback && (
          <div className='mb-4'>
            <label className='block text-sm font-medium text-secondary mb-2'>
              Why are you rejecting this? (optional)
            </label>
            <TextArea
              rows={3}
              value={feedbackMessage}
              onChange={value => setFeedbackMessage(value)}
              placeholder='Provide feedback on why this action should be rejected'
              isDisabled={isSubmitting}
              autoFocus
            />
          </div>
        )}

        {/* Mobile/Tablet: Condensed warning with tooltip (visible only on xs screens) */}
        {mobileWarningContent}

        {/* The chat-error banner moved to the content-region banner stack
            (ChatErrorBanner in ProposalDetailPanel — task 4 part 2b): the
            footer holds only the input and the buttons. */}

        {/* Action Buttons Row - Chat input on left, buttons on right */}
        <div className='flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-4'>
          {/* Left: chat input */}
          {leftCellContent}

          {/* Right: Action Buttons */}
          <div className='flex flex-col items-stretch sm:items-end gap-1 w-full sm:w-auto'>
            {/* min-h-11 (44px) mirrors ConversationInput's simple-variant box
                height (see its py-[11px] comment). The parent row bottom-aligns
                cells (sm:items-end) so a grown multiline chat keeps buttons
                anchored to the chat's last line — centering the 40px buttons
                inside this 44px band makes them sit exactly on the chat
                input's vertical center instead of 2px below it. */}
            <div className='flex items-center gap-2 sm:gap-3 w-full sm:w-auto sm:min-w-[280px] sm:min-h-11'>
              {showRejectFeedback ? (
                <>
                  <Button
                    size='md'
                    color='secondary'
                    onClick={cancelRejectForm}
                    isDisabled={isSubmitting}
                    className='flex-1'
                    data-testid='proposal-cancel-reject-button'
                  >
                    <ShortcutButtonLabel
                      label='Cancel'
                      shortcut={SHORTCUT_LABELS.reject}
                      showShortcut={showShortcuts && !isSubmitting}
                    />
                  </Button>
                  <Button
                    size='md'
                    color='primary-destructive'
                    onClick={handleRejectClick}
                    isDisabled={isSubmitting}
                    isLoading={submittingAction === 'dismiss'}
                    className='flex-1'
                    data-testid='proposal-confirm-reject-button'
                  >
                    <ShortcutButtonLabel
                      label='Confirm rejection'
                      shortcut={SHORTCUT_LABELS.approve}
                      showShortcut={showShortcuts && !isSubmitting}
                    />
                  </Button>
                </>
              ) : (
                <>
                  {/* Hide skip/reject button for pipeline customization and review permissions.
                    The "Saving changes…" tooltip wrapper that used to live here
                    has been removed — the button now stays in its idle visual
                    state during autosave and we don't want a hover-tooltip that
                    contradicts that. The spinner overlay (isLoading) is the only
                    visual signal, and only after the user actually invokes the
                    button. */}
                  {decisionButton}
                  {/* Approve — same rationale as the reject button above: the
                    "Saving changes…" tooltip is gone. The spinner is the only
                    visual signal that something's happening, and only after
                    the user actually invokes the button (click or shortcut).
                    Hidden when the primary action has permanently failed —
                    the footer collapses to a single one-click Dismiss in that
                    state (showPermanentlyFailedDismiss). */}
                  {!showPermanentlyFailedDismiss && (
                    <Button
                      size='md'
                      color='primary'
                      onClick={handleApproveClick}
                      isDisabled={approveBlockedBy !== undefined}
                      // The server-supplied blocked reason surfaces as a
                      // tooltip on the (aria-)disabled button — the footer
                      // renders only the input and the buttons, no reason text
                      // row (task 4 part 2c, 2026-09-01). Passing the prop
                      // switches the button to its gated aria-disabled path,
                      // keeping it focusable so the tooltip works for keyboard
                      // users too. The reason is withheld while an autosave is
                      // in flight — it reflects pre-save state and the save
                      // may be fixing it (same suppression as before).
                      disabledTooltip={approveDisabledTooltip}
                      // A pre-click autosave (hasPendingOrSaving) deliberately
                      // shows NOTHING here: autosave is silent until the user
                      // invokes the button, at which point handleApproveClick
                      // flushes pending saves (isWaitingForSave → spinner) and
                      // then submits. Product decision 2026-09-01 (task 3).
                      isLoading={
                        submittingAction === 'approve' ||
                        isExecuting ||
                        isPolling ||
                        isWaitingForSave
                      }
                      className='flex-1'
                      data-component='action-button'
                      data-instance='approve-proposal'
                      data-blocked-by={approveBlockedBy}
                      data-state={
                        submittingAction === 'approve' ||
                        isExecuting ||
                        isPolling ||
                        isWaitingForSave
                          ? 'saving'
                          : undefined
                      }
                      data-testid='proposal-approve-button'
                    >
                      {/* TODO(i18n): Extract button labels to centralized constants - see docs/technical-debt/LOCALIZATION_PROPOSALS.md */}
                      <ShortcutButtonLabel
                        label={approveButtonLabel}
                        shortcut={SHORTCUT_LABELS.approve}
                        showShortcut={showShortcuts && !isSubmitting}
                      />
                    </Button>
                  )}
                </>
              )}
            </div>
            {/* The blocked reason no longer renders as a text row here — it
              lives in the approve button's disabledTooltip (task 4 part 2c):
              the footer holds only the input and the buttons. */}
          </div>
        </div>
      </div>

      {/* Destructive action warning modal */}
      <DestructiveWarningModal
        isOpen={showDestructiveWarning}
        destructiveActions={destructiveActions}
        isLoading={submittingAction === 'approve'}
        onConfirm={handleDestructiveConfirm}
        onCancel={() => setShowDestructiveWarning(false)}
      />

      {/* Approval confirmation modal — shows summary of all actions */}
      <ApprovalConfirmationModal
        isOpen={showApprovalConfirmation}
        actions={confirmationActions}
        emailRecipients={liveEmailRecipients}
        confirmLabel={approveButtonLabel}
        isLoading={submittingAction === 'approve'}
        orgId={action.org}
        onConfirm={handleApprovalConfirm}
        onCancel={() => {
          setShowApprovalConfirmation(false);
          setConfirmActionsSnapshot(null);
        }}
      />
    </div>
  );
}
